import React, { useEffect, useMemo, useRef, useState } from "react";
import io from "socket.io-client";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import { useAuth } from '../services/authProvider';
import { getAcademicReportRoleLabel, getLevelSuffix } from '../utils/roleLabels';
import { formatTimeShort } from '../utils/teachingUtils';

const API = process.env.REACT_APP_API;

const STATUS_COLORS = {
  present: '#198754',
  late:    '#ffc107',
  leave:   '#0dcaf0',
  absent:  '#dc3545',
};

// เหตุที่ครูไม่อยู่: ลา (leave_type) / ไปอบรม-ประชุมที่อนุมัติแล้ว (training_type)
// / ออกนอกสถานที่ที่อนุมัติแล้วในช่วงเวลาคาบนั้น (offsite_flag)
const getAwayLabel = (p) => p.leave_type
  ? p.leave_type
  : p.training_type
    ? (p.training_type === 'meeting' ? 'ไปประชุม' : p.training_type === 'activity' ? 'ไปกิจกรรม' : 'ไปอบรม')
    : p.offsite_flag
      ? 'ออกนอกสถานที่'
      : null;

// การ์ดห้องที่ยังไม่ได้เช็คชื่อ 1 ห้อง สำหรับมุมมองมือถือ
const UncheckedRoomCard = ({ room }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">ม.{room.c_id}/{room.r_no}</div>
      <Badge bg="secondary" className="fs-xs">{room.p_code}</Badge>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      {room.periods.length > 0 ? room.periods.map((p, i) => {
        const awayLabel = getAwayLabel(p);
        return (
          <div key={i} className="mb-1">
            <span className="text-muted">คาบ {p.period_order}:</span>{' '}
            {p.is_guidance && <Badge bg="info" className="fs-xs me-1">แนะแนว</Badge>}
            {p.subject || <span className="text-muted">-</span>}
            {p.teacher && <span className="text-muted"> · {p.teacher}</span>}
            {awayLabel && <span className="text-danger"> ({awayLabel})</span>}
          </div>
        );
      }) : <span className="text-muted">-</span>}
    </div>
  </div>
)

// การ์ดนักเรียนขาดเรียน 1 คน สำหรับมุมมองมือถือ
const AbsentStudentCard = ({ s }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">{s.st_name}</div>
      {s.st_gen === 'm' ? <Badge bg="primary" className="fs-xs">ชาย</Badge> : <Badge bg="danger" className="fs-xs">หญิง</Badge>}
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">เลขประจำตัว:</span> {s.st_idnumber}</div>
      <div><span className="text-muted">ชั้น:</span> ม.{s.c_id}/{s.r_no} · <span className="text-muted">คาบ:</span> {s.period_order}</div>
    </div>
  </div>
)

export default function GeneralAttendanceReport() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const roleLabel = getAcademicReportRoleLabel(posIds);
  const levelSuffix = getLevelSuffix(posIds);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') {
        for (const th of theads) { th.classList.remove('table-secondary'); th.classList.add('table-primary'); }
        t.classList.add('table-dark');
      } else {
        t.classList.remove('table-dark');
        for (const th of theads) { th.classList.remove('table-primary'); th.classList.add('table-secondary'); }
      }
    }
  };
  useEffect(() => { switchSkin(skin); }, [skin]);

  const today = new Date();
  const todayDate = today.toISOString().split('T')[0];
  const todayTH = today.toLocaleDateString('th-TH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const [lastestYear, setLastestYear]     = useState(null);
  const [periodList, setPeriodList]       = useState([]);
  const [periodId, setPeriodId]           = useState(null);
  const [gender, setGender]               = useState('all');
  const [filterClass, setFilterClass]     = useState('all');
  const [attendanceData, setAttendanceData] = useState([]);
  const [totalStudents, setTotalStudents] = useState({});
  const [absentList, setAbsentList]       = useState([]);
  const [roomStatus, setRoomStatus]           = useState([]);
  const [periodCheckSummary, setPeriodCheckSummary] = useState([]);
  const [summaryData, setSummaryData]         = useState({});
  const [uncheckedView, setUncheckedView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');
  const [absentView, setAbsentView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  useEffect(() => {
    axiosInstance.get(`${API}/getLastestStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => { if (res.data?.year_id) setLastestYear(res.data.year_id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!lastestYear) return;
    axiosInstance.get(`${API}/getPeriodList/${lastestYear}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => { if (res.data?.length > 0) setPeriodList(res.data); })
      .catch(() => {});
  }, [lastestYear]);

  const fetchData = () => {
    if (!lastestYear) return;
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`${API}/getAttendanceSummaryByDateAndYear/${todayDate}/${lastestYear}`, { headers: { authorization: `Bearer ${getToken()}` } }),
      axiosInstance.get(`${API}/getAbsentStudentsByDate/${todayDate}/${lastestYear}`, { headers: { authorization: `Bearer ${getToken()}` } }),
    ])
    .then(([summaryRes, absentRes]) => {
      if (summaryRes.status === 'fulfilled') {
        const sum = summaryRes.value.data;
        if (sum?.summary?.length > 0) setAttendanceData(sum.summary);
        if (sum?.totalStudents?.[0])  setTotalStudents(sum.totalStudents[0]);
      }
      if (absentRes.status === 'fulfilled') setAbsentList(absentRes.value.data || []);
    })
    .finally(() => setLoading(false));
  };

  // Socket handler below is registered once (effect keyed only on lastestYear),
  // so it can't rely on the periodId captured in its closure — it must read the
  // latest value from this ref instead, or a refresh push would reset the
  // period filter back to "all" on screen.
  const periodIdRef = useRef(periodId);
  useEffect(() => { periodIdRef.current = periodId; }, [periodId]);

  const fetchRoomStatus = (pid = periodId) => {
    if (!lastestYear) return;
    const pidPath = pid ? `/${pid}` : '';
    axiosInstance.get(`${API}/getRoomCheckStatusByDate/${todayDate}/${lastestYear}${pidPath}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setRoomStatus(res.data || []))
      .catch(() => {});
  };

  const fetchPeriodCheckSummary = () => {
    if (!lastestYear) return;
    axiosInstance.get(`${API}/getPeriodCheckSummaryByDate/${todayDate}/${lastestYear}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setPeriodCheckSummary(res.data || []))
      .catch(() => {});
  };

  useEffect(() => { fetchData(); fetchPeriodCheckSummary(); }, [lastestYear]);
  useEffect(() => { fetchRoomStatus(); }, [lastestYear, periodId]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('refreshGeneralAttendance', () => {
      fetchData();
      fetchPeriodCheckSummary();
      fetchRoomStatus(periodIdRef.current);
    });
    return () => socket.disconnect();
  }, [lastestYear]);

  useEffect(() => {
    setSummaryData(getAttendanceSummaryByPeriod(attendanceData, periodId));
  }, [periodId, attendanceData]);

  function getAttendanceSummaryByPeriod(data, pid) {
    const filtered = pid ? data.filter(d => d.period_id === pid) : data;
    return filtered.reduce((acc, cur) => {
      const p = n => parseInt(cur[n] || 0, 10);
      acc.totalPresentAll += p('present_count');
      acc.totalAbsentAll  += p('absent_count');
      acc.totalLateAll    += p('late_count');
      acc.totalLeaveAll   += p('leave_count');
      if (cur.st_gen === 'm') {
        acc.totalPresentMale += p('present_count'); acc.totalAbsentMale += p('absent_count');
        acc.totalLateMale    += p('late_count');    acc.totalLeaveMale  += p('leave_count');
      } else if (cur.st_gen === 'f') {
        acc.totalPresentFemale += p('present_count'); acc.totalAbsentFemale += p('absent_count');
        acc.totalLateFemale    += p('late_count');    acc.totalLeaveFemale  += p('leave_count');
      }
      return acc;
    }, {
      totalPresentAll: 0, totalAbsentAll: 0, totalLateAll: 0, totalLeaveAll: 0,
      totalPresentMale: 0, totalAbsentMale: 0, totalLateMale: 0, totalLeaveMale: 0,
      totalPresentFemale: 0, totalAbsentFemale: 0, totalLateFemale: 0, totalLeaveFemale: 0,
    });
  }

  const totalStudent = (g) => {
    if (g === 'm') return Number(totalStudents.total_male || 0);
    if (g === 'f') return Number(totalStudents.total_female || 0);
    return Number(totalStudents.total_all || 0);
  };

  const sv = (type, g) => {
    if (g === 'm') return summaryData['totalPresent'  === type ? 'totalPresentMale'  : type === 'totalAbsent' ? 'totalAbsentMale'  : type === 'totalLate' ? 'totalLateMale'  : 'totalLeaveMale']  || 0;
    if (g === 'f') return summaryData['totalPresent'  === type ? 'totalPresentFemale': type === 'totalAbsent' ? 'totalAbsentFemale': type === 'totalLate' ? 'totalLateFemale': 'totalLeaveFemale'] || 0;
    return summaryData[type + 'All'] || 0;
  };

  const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

  // --- Bar chart: สถิติแต่ละคาบ ---
  const barChartData = useMemo(() => {
    const periods = periodList.filter(p => attendanceData.some(d => d.period_id === p.period_id));
    const cats = periods.map(p => `คาบ ${p.period_order}`);
    const agg = (status) => periods.map(p => {
      const rows = attendanceData.filter(d => d.period_id === p.period_id && (gender === 'all' || d.st_gen === gender));
      return rows.reduce((s, d) => s + parseInt(d[`${status}_count`] || 0, 10), 0);
    });
    return {
      categories: cats,
      series: [
        { name: 'มาเรียน', data: agg('present'), color: STATUS_COLORS.present },
        { name: 'สาย',     data: agg('late'),    color: STATUS_COLORS.late    },
        { name: 'ลา',      data: agg('leave'),   color: STATUS_COLORS.leave   },
        { name: 'ขาด',     data: agg('absent'),  color: STATUS_COLORS.absent  },
      ],
    };
  }, [attendanceData, periodList, gender]);

  const barOptions = {
    chart: { type: 'bar', stacked: false, toolbar: { show: false } },
    colors: [STATUS_COLORS.present, STATUS_COLORS.late, STATUS_COLORS.leave, STATUS_COLORS.absent],
    xaxis: { categories: barChartData.categories, labels: { style: { fontSize: '12px' } } },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
    dataLabels: { enabled: false },
    legend: { position: 'top' },
    grid: { borderColor: 'rgba(72,94,144,0.07)' },
    tooltip: { shared: true, intersect: false },
  };

  // --- Donut chart: สัดส่วนสถานะ ---
  const donutValues = useMemo(() => [
    sv('totalPresent', gender),
    sv('totalLate',    gender),
    sv('totalLeave',   gender),
    sv('totalAbsent',  gender),
  ], [summaryData, gender]);

  const donutOptions = {
    labels: ['มาเรียน', 'สาย', 'ลา', 'ขาด'],
    colors: [STATUS_COLORS.present, STATUS_COLORS.late, STATUS_COLORS.leave, STATUS_COLORS.absent],
    chart: { type: 'donut', toolbar: { show: false } },
    legend: { position: 'bottom' },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` },
    plotOptions: { pie: { donut: { size: '65%' } } },
  };

  // --- Absent list filters ---
  const classLevels = useMemo(() => [...new Set(absentList.map(s => s.c_class))].sort(), [absentList]);
  const absentPeriods = useMemo(() => [...new Set(absentList.map(s => s.period_id))], [absentList]);

  // Deduplicate rows by r_id, collecting periods array per room.
  // A room is considered unchecked if any of its periods are still unchecked.
  const allRoomsMapped = useMemo(() => {
    const map = new Map();
    roomStatus.forEach(row => {
      if (!map.has(row.r_id)) {
        map.set(row.r_id, {
          r_id: row.r_id,
          r_no: row.r_no,
          c_id: row.c_id,
          c_class: row.c_class,
          p_code: row.p_code,
          check_status: row.check_status,
          periods: [],
        });
      }
      if (row.period_order != null) {
        map.get(row.r_id).periods.push({
          period_order: row.period_order,
          subject: row.subject,
          teacher: row.teacher,
          check_status: row.check_status,
          leave_type: row.leave_type,
          training_type: row.training_type,
          offsite_flag: row.offsite_flag,
          is_guidance: row.is_guidance === 1,
        });
      }
    });

    return [...map.values()].map(room => {
      const uncheckedPeriods = room.periods.filter(p => p.check_status === 'unchecked');
      // No period row under the current filter = the room has no scheduled
      // class (in the selected period / today), so it can never be checked —
      // don't count it as unchecked.
      const roomCheckStatus = room.periods.length
        ? (uncheckedPeriods.length > 0 ? 'unchecked' : 'checked')
        : 'no_class';

      return {
        ...room,
        check_status: roomCheckStatus,
        periods: uncheckedPeriods.length > 0 ? uncheckedPeriods : room.periods,
      };
    });
  }, [roomStatus]);

  const checkedRooms   = useMemo(() => allRoomsMapped.filter(r => r.check_status === 'checked'),   [allRoomsMapped]);
  const uncheckedRooms = useMemo(() => allRoomsMapped.filter(r => r.check_status === 'unchecked'), [allRoomsMapped]);
  const noClassRooms   = useMemo(() => allRoomsMapped.filter(r => r.check_status === 'no_class'),  [allRoomsMapped]);
  const uncheckedByClass = useMemo(() => {
    const map = new Map();
    uncheckedRooms.forEach(r => {
      if (!map.has(r.c_id)) map.set(r.c_id, { c_id: r.c_id, c_class: r.c_class, rooms: [] });
      map.get(r.c_id).rooms.push(r);
    });
    return [...map.values()].sort((a, b) => a.c_id - b.c_id);
  }, [uncheckedRooms]);
  const totalRooms = allRoomsMapped.length;

  const makePeriodDonutOptions = (checked, total, label) => ({
    labels: ['เช็คแล้ว', 'ยังไม่เช็ค', 'ไม่มีคาบ'],
    colors: ['#198754', '#dc3545', '#adb5bd'],
    chart: { type: 'donut', toolbar: { show: false }, animations: { enabled: false } },
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: '68%', labels: {
      show: true,
      name: { show: true, fontSize: '10px', offsetY: -4 },
      value: { show: true, fontSize: '13px', fontWeight: 700, offsetY: 4, formatter: () => `${checked}/${total}` },
      total: { show: true, label, fontSize: '10px', color: '#6c757d', formatter: () => `${checked}/${total}` },
    } } } },
    tooltip: { y: { formatter: v => `${v} ห้อง` } },
    stroke: { width: 1 },
  });

  const filteredAbsent = useMemo(() => absentList.filter(s =>
    (gender === 'all' || s.st_gen === gender) &&
    (filterClass === 'all' || s.c_class === filterClass) &&
    (!periodId || s.period_id === periodId)
  ), [absentList, gender, filterClass, periodId]);

  // --- KPI cards ---
  const total    = totalStudent(gender);
  const present  = sv('totalPresent', gender);
  const late     = sv('totalLate',    gender);
  const leave    = sv('totalLeave',   gender);
  const absent   = sv('totalAbsent',  gender);

  // With "ทุกคาบ" (periodId null) the status counts are summed across every
  // period, so a student checked in multiple periods is counted more than once
  // → the unit is "ครั้ง". With a single period each student appears once → "คน".
  const statusUnit = periodId ? 'คน' : 'ครั้ง';

  const kpis = [
    { label: 'นักเรียนทั้งหมด', value: total,   color: 'primary', icon: 'ri-group-line',       pct: 100,                unit: 'คน' },
    { label: 'มาเรียน',         value: present, color: 'success', icon: 'ri-checkbox-circle-line', pct: pct(present, total), unit: statusUnit },
    { label: 'สาย',             value: late,    color: 'warning', icon: 'ri-time-line',         pct: pct(late,    total), unit: statusUnit },
    { label: 'ลา',              value: leave,   color: 'info',    icon: 'ri-file-list-3-line',  pct: pct(leave,   total), unit: statusUnit },
    { label: 'ขาดเรียน',        value: absent,  color: 'danger',  icon: 'ri-close-circle-line', pct: pct(absent,  total), unit: statusUnit },
  ];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabel}</Link></li>
              <li className="breadcrumb-item active">รายงานการเข้าเรียนวันนี้</li>
            </ol>
            <h4 className="main-title mb-0">Dashboard การเข้าเรียน{levelSuffix}</h4>
            <p className="text-muted fs-sm mb-0">{todayTH}</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {/* Period filter */}
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 200, borderColor: '#0d6efd', color: '#0d6efd' }}
              value={periodId || ''}
              onChange={e => setPeriodId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">ทุกคาบ</option>
              {periodList.map(p => (
                <option key={p.period_id} value={p.period_id}>
                  คาบ {p.period_order} ({formatTimeShort(p.period_begin)}–{formatTimeShort(p.period_end)})
                </option>
              ))}
            </select>

            {/* Gender filter */}
            <div className="btn-group btn-group-sm">
              {[['all','ทั้งหมด'],['m','ชาย'],['f','หญิง']].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${gender === v ? '' : '-outline'}-primary`}
                  onClick={() => setGender(v)}
                >{label}</button>
              ))}
            </div>

            {/* Refresh */}
            <button className="btn btn-sm btn-outline-secondary" onClick={fetchData} title="รีเฟรช">
              <i className="ri-refresh-line"></i>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          {kpis.map(k => (
            <Col key={k.label} xs={6} sm={4} xl>
              <Card className={`card-one border-${k.color} border-start border-4`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-1`}>{k.value} <span className="fs-sm fw-normal text-muted">{k.unit}</span></h3>
                  <div className="progress" style={{ height: 6 }}>
                    <div className={`progress-bar bg-${k.color}`} style={{ width: `${k.pct}%` }} />
                  </div>
                  <div className="text-end fs-xs text-muted mt-1">{k.pct}%</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts row */}
        <Row className="g-3 mb-4">
          <Col xl={8}>
            <Card className="card-one h-100">
              <Card.Header>
                <Card.Title as="h6">สถิติการเข้าเรียนรายคาบ</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {barChartData.categories.length > 0
                  ? <ReactApexChart series={barChartData.series} options={barOptions} type="bar" height={260} />
                  : <div className="text-center text-muted py-5">ยังไม่มีข้อมูลการเช็คชื่อวันนี้</div>
                }
              </Card.Body>
            </Card>
          </Col>
          <Col xl={4}>
            <Card className="card-one h-100">
              <Card.Header>
                <Card.Title as="h6">
                  สัดส่วนสถานะ
                  {periodId && <span className="text-muted fs-xs ms-2">
                    คาบ {periodList.find(p => p.period_id === periodId)?.period_order}
                  </span>}
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3 d-flex align-items-center justify-content-center">
                {donutValues.some(v => v > 0)
                  ? <ReactApexChart series={donutValues} options={donutOptions} type="donut" height={260} />
                  : <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
                }
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Room check status */}
        <Row className="g-3 mb-4">
          <Col xl={4}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">สถานะการเช็คชื่อรายคาบ</Card.Title>
                <span className="text-muted fs-xs ms-auto">{totalRooms} ห้องทั้งหมด</span>
              </Card.Header>
              <Card.Body className="p-3">
                {periodCheckSummary.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-time-line fs-3 d-block mb-1"></i>
                    ยังไม่มีคาบที่เช็คชื่อวันนี้
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-3 justify-content-center">
                    {periodCheckSummary.map(p => {
                      const scheduled = Number(p.scheduled_rooms);
                      const checked   = Number(p.checked_rooms);
                      const unchecked = Math.max(0, scheduled - checked);
                      const noClass   = Math.max(0, totalRooms - scheduled);
                      const pct       = scheduled > 0 ? Math.round((checked / scheduled) * 100) : 0;
                      return (
                        <div key={p.period_id} className="text-center" style={{ width: 115 }}>
                          <div className="fs-xs fw-semibold text-secondary mb-0">คาบ {p.period_order}</div>
                          <div className="fs-xs text-muted mb-1" style={{ fontSize: '10px' }}>{formatTimeShort(p.period_begin)}–{formatTimeShort(p.period_end)}</div>
                          <ReactApexChart
                            series={[checked, unchecked, noClass]}
                            options={makePeriodDonutOptions(checked, scheduled, `คาบ ${p.period_order}`)}
                            type="donut"
                            height={110}
                            width="100%"
                          />
                          <div className="fs-xs mt-1">
                            <span className="text-success fw-semibold">{checked}</span>
                            <span className="text-muted"> / {scheduled} ({pct}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {periodCheckSummary.length > 0 && (
                  <div className="d-flex flex-wrap justify-content-center gap-3 fs-xs text-muted mt-2 pt-2 border-top">
                    <span><i className="ri-checkbox-blank-circle-fill me-1" style={{ color: '#198754' }} />เช็คแล้ว</span>
                    <span><i className="ri-checkbox-blank-circle-fill me-1" style={{ color: '#dc3545' }} />ยังไม่เช็ค</span>
                    <span><i className="ri-checkbox-blank-circle-fill me-1" style={{ color: '#adb5bd' }} />ไม่มีคาบ</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xl={8}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">ห้องที่ยังไม่ได้เช็คชื่อ</Card.Title>
                <Badge bg="danger" className="ms-1">{uncheckedRooms.length} ห้อง</Badge>
                <Badge bg="success" className="ms-1">เช็คแล้ว {checkedRooms.length} ห้อง</Badge>
                {noClassRooms.length > 0 && (
                  <Badge bg="secondary" className="ms-1">ไม่มีคาบเรียน {noClassRooms.length} ห้อง</Badge>
                )}
                <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${uncheckedView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setUncheckedView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${uncheckedView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setUncheckedView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {uncheckedRooms.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-checkbox-circle-line fs-3 text-success d-block mb-1"></i>
                    ทุกห้องเช็คชื่อครบแล้ว
                  </div>
                ) : uncheckedView === 'card' ? (
                  <div className="p-3" style={{ maxHeight: 480, overflowY: 'auto' }}>
                    {uncheckedByClass.map(group => (
                      <div key={group.c_id} className="mb-3">
                        <div className="fw-semibold text-secondary mb-2">
                          ชั้น ม.{group.c_id} <Badge bg="danger" className="ms-1 fs-xs">{group.rooms.length} ห้อง</Badge>
                        </div>
                        <Row className="g-3">
                          {group.rooms.map(room => (
                            <Col key={room.r_id} xs={12} md={6} xl={4}>
                              <UncheckedRoomCard room={room} />
                            </Col>
                          ))}
                        </Row>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead className="table-secondary sticky-top">
                        <tr>
                          <th className="text-center" style={{ width: 90 }}>ห้อง</th>
                          <th className="text-center" style={{ width: 75 }}>โปรแกรม</th>
                          <th className="text-center" style={{ width: 70 }}>คาบที่</th>
                          <th>วิชา</th>
                          <th>ครูผู้สอน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uncheckedByClass.map(group => (
                          <React.Fragment key={group.c_id}>
                            <tr className="table-light">
                              <td colSpan={5} className="fw-semibold text-secondary ps-3 py-1">
                                ชั้น ม.{group.c_id}
                                <Badge bg="danger" className="ms-2 fs-xs">{group.rooms.length} ห้อง</Badge>
                              </td>
                            </tr>
                            {group.rooms.map(room => {
                              const rows = room.periods.length > 0 ? room.periods : [null];
                              return rows.map((period, pi) => (
                                <tr key={`${room.r_id}-${pi}`}>
                                  {pi === 0 && (
                                    <>
                                      <td rowSpan={rows.length} className="align-middle fw-semibold text-center">ม.{room.c_id}/{room.r_no}</td>
                                      <td rowSpan={rows.length} className="align-middle text-center">
                                        <Badge bg="secondary" className="fs-xs">{room.p_code}</Badge>
                                      </td>
                                    </>
                                  )}
                                  <td className="text-center">{period ? period.period_order : <span className="text-muted">-</span>}</td>
                                  <td className="fs-sm">
                                    {period?.is_guidance && <Badge bg="info" className="fs-xs me-1">แนะแนว</Badge>}
                                    {period?.subject || <span className="text-muted">-</span>}
                                  </td>
                                  <td className="fs-sm">
                                    {period?.teacher || <span className="text-muted">-</span>}
                                    {period && getAwayLabel(period) && (
                                      <span className="text-danger"> ({getAwayLabel(period)})</span>
                                    )}
                                  </td>
                                </tr>
                              ));
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Absent list */}
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">
              รายชื่อนักเรียนที่ขาดเรียนวันนี้
              <Badge bg="danger" className="ms-2">{filteredAbsent.length} คน</Badge>
            </Card.Title>
            <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
              {/* Class filter */}
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 130 }}
                value={filterClass}
                onChange={e => setFilterClass(e.target.value)}
              >
                <option value="all">ทุกชั้น</option>
                {classLevels.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="text-muted fs-xs">
                (แสดงตามตัวกรองคาบ/ชั้น/เพศด้านบน)
              </span>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${absentView === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setAbsentView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${absentView === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setAbsentView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {filteredAbsent.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="ri-emotion-happy-line fs-3 d-block mb-1"></i>
                ไม่พบนักเรียนที่ขาดเรียน{periodId ? 'ในคาบนี้' : 'วันนี้'}
              </div>
            ) : absentView === 'card' ? (
              <div className="p-3" style={{ maxHeight: 480, overflowY: 'auto' }}>
                <Row className="g-3">
                  {filteredAbsent.map((s, i) => (
                    <Col key={`${s.st_id}-${s.period_id}`} xs={12} md={6} xl={4}>
                      <AbsentStudentCard s={s} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th className="text-center" style={{ width: 50 }}>#</th>
                      <th className="text-center" style={{ width: 110 }}>เลขประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th className="text-center" style={{ width: 70 }}>เพศ</th>
                      <th className="text-center" style={{ width: 80 }}>ชั้น</th>
                      <th className="text-center" style={{ width: 70 }}>คาบที่</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAbsent.map((s, i) => (
                      <tr key={`${s.st_id}-${s.period_id}`}>
                        <td className="text-center">{i + 1}</td>
                        <td className="text-center">{s.st_idnumber}</td>
                        <td>{s.st_name}</td>
                        <td className="text-center">
                          {s.st_gen === 'm'
                            ? <Badge bg="primary" className="fs-xs">ชาย</Badge>
                            : <Badge bg="danger"  className="fs-xs">หญิง</Badge>}
                        </td>
                        <td className="text-center">ม.{s.c_id}/{s.r_no}</td>
                        <td className="text-center">{s.period_order}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  );
}
